export const INLINE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
export const INLINE_IMAGE_LIMIT = 10 * 1024 * 1024;
export const INLINE_IMAGE_CONCURRENCY = 4;

export function inlineImageUrl(value) {
  try {
    const url = new URL(String(value || ''));
    if (/^https:\/\/(?:www\.)?antrazal\.com\/backend\/(?:awc\/v1\/hrm\/files|sow\/projects\/inline-images)\/069[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?\/content$/.test(String(value))) return url.href;
    return url.protocol === 'https:' && !url.username && !url.password && !url.port && !url.hash
      && /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:file\.force\.com|documentforce\.com)$/.test(url.hostname)
      && /^\/sfc\/dist\/version\/download(?:\/(?:[A-Za-z0-9_-]+\/?)?)?$/.test(url.pathname) ? url.href : '';
  } catch { return ''; }
}

export function clipboardImages(data) {
  if (!data) return [];
  const files = Array.from(data.files || []);
  if (!files.length) for (const item of Array.from(data.items || [])) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files.filter(file => /^image\//i.test(file.type));
}

export function clipboardHasImages(data) {
  return clipboardImages(data).length > 0 || /<(?:img|v:imagedata)\b/i.test(data?.getData?.('text/html') || '');
}

function clipboardImageFile(source, name) {
  const match = /^data:(image\/(?:png|jpeg|gif|webp));base64,([a-z0-9+/=\s]+)$/i.exec(source);
  if (!match) throw new Error('Word did not include this image in the clipboard. Choose its image file or import the Word document.');
  if (match[2].length > Math.ceil(INLINE_IMAGE_LIMIT / 3) * 4 + 1024) throw new Error('Choose an image between 1 byte and 10 MB.');
  let raw;
  try { raw = atob(match[2].replace(/\s/g, '')); } catch { throw new Error('The pasted image data is incomplete. Copy it again or import the Word document.'); }
  const bytes = Uint8Array.from(raw, character => character.charCodeAt(0));
  return new File([bytes], name, { type: match[1].toLowerCase() });
}

// Image sources are captured in an inert template, never inserted into the live
// DOM. Only independently sanitized text and controller-owned placeholders leave it.
function clipboardFragment(data, sanitizeHtml) {
  const raw = data?.getData?.('text/html') || '', files = clipboardImages(data);
  const template = document.createElement('template'); template.innerHTML = raw;
  const images = [...template.content.querySelectorAll('img, v\\:imagedata')];
  const unresolved = images.filter(image => !inlineImageUrl(image.getAttribute('src')) && !/^data:/i.test(image.getAttribute('src') || ''));
  const used = new Set(), specs = [];
  for (const image of images) {
    const source = image.getAttribute('src') || '';
    if (inlineImageUrl(source)) continue;
    const name = (image.getAttribute('alt') || '').slice(0, 180) || `Pasted image ${specs.length + 1}.png`;
    let file = files.find(file => !used.has(file) && (file.name === name || source.endsWith('/' + file.name)));
    if (!file && !/^data:/i.test(source) && files.length === unresolved.length) file = files[unresolved.indexOf(image)];
    if (file) used.add(file);
    const marker = `inline-image-${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}-${specs.length}`;
    const size = attribute => Math.min(16000, Math.max(1, parseInt(image.getAttribute(attribute), 10) || 0));
    specs.push({ file, source, name, marker, display: image.hasAttribute('width') && image.hasAttribute('height')
      ? { width: size('width'), height: size('height') } : null });
    image.replaceWith(document.createTextNode(marker));
  }
  if (!raw) {
    template.content.textContent = data?.getData?.('text/plain') || '';
    template.innerHTML = template.innerHTML.replace(/\r\n?|\n/g, '<br>');
  }
  // A browser may include a rendered preview File alongside a complete rich
  // document. Image positions in HTML take precedence over that preview.
  if (!images.length) for (const file of files) {
    const marker = `inline-image-${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}-${specs.length}`;
    specs.push({ file, name: file.name, marker });
    template.content.append(document.createTextNode(marker), document.createElement('br'));
  }
  const cleaned = document.createElement('template');
  if (sanitizeHtml) cleaned.innerHTML = sanitizeHtml(template.innerHTML);
  else if (!raw) cleaned.content.append(template.content.cloneNode(true));
  else cleaned.content.textContent = template.content.textContent;
  return { fragment: cleaned.content, specs: specs.filter(spec => cleaned.content.textContent.includes(spec.marker)) };
}

export function cleanInlineImages(root) {
  root.querySelectorAll('[data-inline-upload]').forEach(node => node.remove());
  root.querySelectorAll('img').forEach(node => {
    const src = inlineImageUrl(node.getAttribute('src'));
    if (!src) { node.remove(); return; }
    const alt = node.getAttribute('alt') || 'Pasted image';
    const width = Math.min(16000, Math.max(1, parseInt(node.getAttribute('width'), 10) || 640));
    const height = Math.min(16000, Math.max(1, parseInt(node.getAttribute('height'), 10) || 360));
    Array.from(node.attributes).forEach(attribute => node.removeAttribute(attribute.name));
    node.setAttribute('src', src);
    node.setAttribute('alt', alt.slice(0, 180));
    node.setAttribute('width', String(width));
    node.setAttribute('height', String(height));
    node.setAttribute('loading', 'lazy');
    node.setAttribute('referrerpolicy', 'no-referrer');
  });
}

export function hasInlineImage(html) {
  const template = document.createElement('template');
  template.innerHTML = String(html || '');
  return Array.from(template.content.querySelectorAll('img')).some(img => inlineImageUrl(img.getAttribute('src')));
}

export function inlineFilesForHtml(files, html) {
  const template = document.createElement('template');
  template.innerHTML = String(html || '');
  const urls = new Set(Array.from(template.content.querySelectorAll('img')).map(img => inlineImageUrl(img.getAttribute('src'))).filter(Boolean));
  return files.filter(file => urls.has(inlineImageUrl(file.contentDownloadUrl)));
}

export function inlineEditorHtml(editor) {
  const copy = editor.cloneNode(true);
  cleanInlineImages(copy);
  return copy.innerHTML;
}

function imageDimensions(url, signal) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    let timer, settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', aborted);
      image.onload = image.onerror = null;
      if (error) reject(error);
      else if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth > 16000 || image.naturalHeight > 16000
        || image.naturalWidth * image.naturalHeight > 40000000) reject(new Error('Choose an image smaller than 40 megapixels.'));
      else resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    const aborted = () => finish(new Error('Image paste cancelled.'));
    image.onload = () => finish();
    image.onerror = () => finish(new Error('This image could not be read. Choose a PNG, JPEG, GIF or WebP image.'));
    timer = setTimeout(() => finish(new Error('Reading this image timed out. Retry or remove it.')), 20000);
    signal.addEventListener('abort', aborted, { once: true });
    if (signal.aborted) { aborted(); return; }
    image.src = url;
  });
}

// A placeholder belongs to one editor and one upload. Never serialize transient
// nodes or replace the whole editor when an asynchronous upload finishes.
export function createInlineImagePaste(editor, options) {
  const tasks = new Map();
  let disposed = false;
  let running = 0;
  const pump = () => {
    if (disposed) return;
    for (const task of tasks.values()) {
      if (running >= INLINE_IMAGE_CONCURRENCY) break;
      if (!task.running && !task.error) run(task);
    }
  };
  const warnBeforeLeaving = event => {
    if (!tasks.size) return;
    event.preventDefault(); event.returnValue = '';
  };
  window.addEventListener('beforeunload', warnBeforeLeaving);
  const notify = () => {
    if (disposed) return;
    editor.dataset.inlineImagesPending = String(tasks.size);
    options.onState?.({ pending: tasks.size, failed: [...tasks.values()].filter(task => task.error).length,
      uploads: [...tasks.values()].map(task => ({
        id: task.id, name: task.name, bytes: task.file?.size || 0, type: task.file?.type || '',
        previewUrl: task.previewUrl || '', status: task.status, progress: task.progress.value, error: task.error || '',
      })) });
  };
  const emit = () => { if (!disposed) options.onChange?.(inlineEditorHtml(editor)); };
  const current = task => !disposed && tasks.get(task.id) === task && editor.contains(task.node) && !task.abort.signal.aborted;
  const progress = (task, percent, label) => {
    if (!current(task)) return;
    task.progress.value = Math.min(99, Math.max(0, Number(percent) || 0));
    task.status = label ? 'loading' : task.transfer.uploaded || Number(percent) >= 100 ? 'publishing' : 'uploading';
    task.label.textContent = label || `Uploading ${task.file.name} (${Math.round(task.progress.value)}%)`;
    notify();
  };
  const releasePreview = task => {
    task.preview?.remove();
    task.preview = null;
    if (task.previewUrl) URL.revokeObjectURL(task.previewUrl);
    task.previewUrl = '';
    task.node.classList.remove('inline-image-upload--preview');
  };
  const discard = task => {
    tasks.delete(task.id);
    task.abort.abort();
    releasePreview(task);
    task.node.remove();
  };
  const remove = task => {
    if (!task || tasks.get(task.id) !== task) return false;
    discard(task);
    notify(); emit(); pump();
    return true;
  };
  const fail = (task, error) => {
    task.error = error.message || 'The image could not be uploaded.';
    task.status = 'failed';
    task.node.classList.add('inline-image-upload--failed');
    task.node.setAttribute('aria-busy', 'false');
    task.label.textContent = task.error;
    task.retry.hidden = false;
  };
  const prepare = task => {
    task.file ||= clipboardImageFile(task.source || '', task.name);
    if (!INLINE_IMAGE_TYPES.has(task.file.type.toLowerCase())) throw new Error('Use PNG, JPEG, GIF or WebP. SVG images are not supported.');
    if (!task.file.size || task.file.size > INLINE_IMAGE_LIMIT) throw new Error('Choose an image between 1 byte and 10 MB.');
    if (!task.previewUrl) {
      task.previewUrl = URL.createObjectURL(task.file);
      task.preview = document.createElement('img');
      task.preview.className = 'inline-image-upload__preview';
      task.preview.dataset.inlinePreview = task.id;
      task.preview.alt = task.name;
      task.preview.src = task.previewUrl;
      task.node.prepend(task.preview);
      task.node.classList.add('inline-image-upload--preview');
    }
  };
  const retryTask = task => {
    if (!task || !current(task) || task.running || !task.error) return false;
    task.error = null;
    task.status = 'queued';
    task.progress.value = 0;
    task.label.textContent = 'Waiting to upload...';
    task.retry.hidden = true;
    task.node.classList.remove('inline-image-upload--failed');
    task.node.setAttribute('aria-busy', 'true');
    notify(); pump();
    return true;
  };
  const run = async task => {
    if (!current(task) || task.running) return;
    if (running >= INLINE_IMAGE_CONCURRENCY) return;
    running++;
    task.running = true;
    task.error = null;
    task.status = 'reading';
    task.label.textContent = 'Reading image...';
    task.node.classList.remove('inline-image-upload--failed');
    task.node.setAttribute('aria-busy', 'true');
    task.retry.hidden = true;
    notify();
    try {
      if (!current(task)) return;
      prepare(task);
      task.dimensions ||= await imageDimensions(task.previewUrl, task.abort.signal);
      if (!current(task)) return;
      const display = task.display || task.dimensions;
      task.node.style.width = `${display.width}px`;
      task.node.style.aspectRatio = `${display.width} / ${display.height}`;
      progress(task, 0);
      if (!current(task)) return;
      task.result ||= await options.upload(task.file, {
        signal: task.abort.signal,
        onProgress: percent => progress(task, percent),
        transfer: task.transfer,
      });
      if (!current(task)) return;
      const src = inlineImageUrl(task.result?.contentDownloadUrl);
      if (!src) {
        task.result = null;
        throw new Error('Salesforce did not return a direct image link. Retry to finish attaching this image.');
      }
      const image = document.createElement('img');
      image.alt = task.name || task.file.name;
      image.width = display.width;
      image.height = display.height;
      image.referrerPolicy = 'no-referrer';
      progress(task, 99, 'Loading image...');
      await new Promise((resolve, reject) => {
        let settled = false;
        const timer = setTimeout(() => finish(new Error('The image link is not ready. Retry without uploading it again.')), 25000);
        const aborted = () => finish(new Error('Image paste cancelled.'));
        const finish = error => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          task.abort.signal.removeEventListener('abort', aborted);
          image.onload = image.onerror = null;
          error ? reject(error) : resolve();
        };
        image.onload = () => finish();
        image.onerror = () => finish(new Error('The image link could not load. Retry without uploading it again.'));
        task.abort.signal.addEventListener('abort', aborted, { once: true });
        if (task.abort.signal.aborted) { aborted(); return; }
        image.src = src;
      });
      if (!current(task)) return;
      options.onUploaded?.(task.result);
      if (!current(task)) return;
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const caretAfterImage = range?.collapsed && range.startContainer === task.node.parentNode
        && range.startOffset === Array.prototype.indexOf.call(task.node.parentNode.childNodes, task.node) + 1;
      task.node.replaceWith(image);
      if (caretAfterImage) {
        range.setStartAfter(image); range.collapse(true);
        selection.removeAllRanges(); selection.addRange(range);
      }
      tasks.delete(task.id);
      releasePreview(task);
      notify(); emit();
    } catch (error) {
      if (!current(task)) return;
      fail(task, error);
      notify();
    } finally { task.running = false; running--; pump(); }
  };
  return {
    get pending() { return tasks.size; },
    retry(id) { return retryTask(tasks.get(id)); },
    remove(id) { return remove(tasks.get(id)); },
    paste(event) {
      if (disposed || !clipboardHasImages(event.clipboardData) || editor.getAttribute('contenteditable') === 'false') return false;
      event.preventDefault();
      event.stopPropagation();
      if ((event.clipboardData?.getData?.('text/html') || '').length > 32 * 1024 * 1024
        || clipboardImages(event.clipboardData).reduce((size, file) => size + file.size, 0) > 32 * 1024 * 1024) {
        options.onError?.('This clipboard content exceeds 32 MB. Paste a smaller selection or import the Word document. Nothing was replaced.');
        return true;
      }
      const { fragment, specs } = clipboardFragment(event.clipboardData, options.sanitizeHtml);
      const maxImages = options.maxImages?.() ?? 100;
      if (specs.length + tasks.size > maxImages) {
        options.onError?.(`This paste contains too many images. Up to ${maxImages} additional images can be included. Nothing was replaced.`);
        return true;
      }
      const selection = window.getSelection();
      const range = selection?.rangeCount && editor.contains(selection.getRangeAt(0).commonAncestorContainer)
        ? selection.getRangeAt(0).cloneRange() : document.createRange();
      if (!editor.contains(range.commonAncestorContainer)) { range.selectNodeContents(editor); range.collapse(false); }
      const added = specs.map(spec => {
        const { file, name, marker, display } = spec;
        const id = `paste-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
        const node = document.createElement('span');
        node.className = 'inline-image-upload';
        node.dataset.inlineUpload = id;
        node.contentEditable = 'false';
        node.setAttribute('aria-busy', 'true');
        node.setAttribute('role', 'group');
        node.setAttribute('aria-label', `Image upload: ${name}`);
        node.style.width = display ? `${display.width}px` : '320px';
        node.style.aspectRatio = display ? `${display.width} / ${display.height}` : '16 / 9';
        const label = document.createElement('span'); label.className = 'inline-image-upload__label';
        label.setAttribute('role', 'status'); label.textContent = 'Waiting to upload...';
        const meter = document.createElement('progress'); meter.max = 100; meter.value = 0;
        meter.setAttribute('aria-label', `Upload progress: ${name}`);
        const controls = document.createElement('span'); controls.className = 'inline-image-upload__controls';
        const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry'; retry.hidden = true;
        const discard = document.createElement('button'); discard.type = 'button'; discard.textContent = 'Remove';
        discard.setAttribute('aria-label', `Remove pasted image ${name}`);
        const choose = document.createElement('label'); choose.textContent = 'Choose image';
        const input = document.createElement('input'); input.type = 'file'; input.accept = [...INLINE_IMAGE_TYPES].join(',');
        input.setAttribute('aria-label', `Choose file for ${name}`); choose.append(input);
        controls.append(retry, discard, choose); node.append(label, meter, controls);
        const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
        let target;
        while (walker.nextNode()) if (walker.currentNode.data.includes(marker)) { target = walker.currentNode; break; }
        if (!target) throw new Error('The pasted image position could not be preserved.');
        const position = target.splitText(target.data.indexOf(marker)), tail = position.splitText(marker.length);
        position.replaceWith(node);
        if (!target.data) target.remove();
        if (!tail.data) tail.remove();
        const task = { ...spec, id, file, node, label, progress: meter, retry, status: 'queued', abort: new AbortController(), transfer: {} };
        retry.onclick = event => { event.preventDefault(); event.stopPropagation(); retryTask(task); };
        discard.onclick = event => { event.preventDefault(); event.stopPropagation(); remove(task); };
        input.onchange = () => {
          if (!input.files?.[0] || !current(task) || task.running) return;
          task.abort.abort(); releasePreview(task);
          task.abort = new AbortController();
          task.file = input.files[0]; task.name = task.file.name;
          task.dimensions = null; task.result = null; task.transfer = {};
          try { prepare(task); } catch (error) { fail(task, error); notify(); return; }
          retryTask(task);
        };
        tasks.set(id, task);
        return task;
      });
      const end = document.createTextNode(''); fragment.append(end);
      range.deleteContents(); range.insertNode(fragment);
      for (const task of tasks.values()) if (!editor.contains(task.node)) discard(task);
      // Preview every queued image before upload workers can block on decoding or I/O.
      for (const task of added) {
        try { prepare(task); } catch (error) { fail(task, error); }
      }
      // Preserve the image-only caret behavior while whole documents continue
      // after their final paragraph, not after their last embedded image.
      const onlyFiles = !(event.clipboardData?.getData?.('text/html') || event.clipboardData?.getData?.('text/plain'));
      range.setStartAfter(onlyFiles && added.length ? added[added.length - 1].node : end); range.collapse(true);
      selection?.removeAllRanges(); selection?.addRange(range);
      notify(); emit();
      pump();
      return true;
    },
    reconcile() {
      for (const task of tasks.values()) if (!editor.contains(task.node)) remove(task);
    },
    dispose() {
      disposed = true;
      window.removeEventListener('beforeunload', warnBeforeLeaving);
      for (const task of tasks.values()) discard(task);
      delete editor.dataset.inlineImagesPending;
    },
  };
}
